/**
 * Botão único de entrada da prescrição digital Memed.
 *
 * Usado no Receituário e na Evolução para que os dois fluxos tenham exatamente
 * o mesmo comportamento: mesma sessão, mesmos toggles e a mesma persistência da
 * receita no prontuário.
 */

import React, { useEffect } from 'react';
import { Alert, Button, CircularProgress, Snackbar, Tooltip } from '@mui/material';
import { VerifiedUser as SignatureIcon } from '@mui/icons-material';
import { useMemed } from '../../hooks/useMemed';

export interface MemedPrescriptionButtonProps {
  patientId?: string | null;
  professionalId?: string | null;
  appointmentId?: string | null;
  medicalRecordId?: string | null;
  /** Disparado após a receita ser gravada em `clinic_prescriptions`. */
  onSaved?: (prescription: any, meta: { reprinted: boolean }) => void;
  label?: string;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  disabled?: boolean;
  /** Motivo exibido no tooltip quando o botão está bloqueado. */
  disabledReason?: string;
  /** `subtle`: opção secundária, discreta (ex.: dentro da evolução). */
  prominence?: 'default' | 'subtle';
  sx?: any;
}

const MEMED_PURPLE = '#5b21b6';

/** @deprecated Use `prominence="subtle"` — mantido para não quebrar `sx` legado. */
export const memedPrescriptionPanelButtonSx = {
  color: '#94a3b8',
  textTransform: 'none' as const,
  fontWeight: 500,
  fontSize: '0.75rem',
  minWidth: 0,
  px: 0.75,
  py: 0.25,
  border: 'none',
  '&:hover': { bgcolor: 'transparent', color: '#64748b', textDecoration: 'underline' },
};

const defaultOutlinedSx = {
  borderColor: '#e2e8f0',
  color: '#64748b',
  textTransform: 'none' as const,
  fontWeight: 500,
  fontSize: '0.8125rem',
  '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
};

const defaultContainedSx = {
  bgcolor: MEMED_PURPLE,
  textTransform: 'none' as const,
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(91, 33, 182, 0.24)',
  '&:hover': { bgcolor: '#4c1d95' },
};

const subtleSx = {
  color: '#94a3b8',
  textTransform: 'none' as const,
  fontWeight: 500,
  fontSize: '0.75rem',
  minWidth: 0,
  px: 0.75,
  py: 0.25,
  '&:hover': { bgcolor: 'transparent', color: '#64748b', textDecoration: 'underline' },
};

export const MemedPrescriptionButton: React.FC<MemedPrescriptionButtonProps> = ({
  patientId,
  professionalId,
  appointmentId,
  medicalRecordId,
  onSaved,
  label,
  variant,
  size,
  fullWidth = false,
  disabled = false,
  disabledReason,
  prominence = 'subtle',
  sx,
}) => {
  const isSubtle = prominence === 'subtle';
  const resolvedLabel = label ?? (isSubtle ? 'Prescrição via Memed (opcional)' : 'Receita digital (Memed)');
  const resolvedVariant = variant ?? (isSubtle ? 'text' : 'outlined');
  const resolvedSize = size ?? (isSubtle ? 'small' : 'medium');
  const memed = useMemed({ professionalId, onPrescriptionSaved: onSaved });

  useEffect(() => {
    if (!memed.available || !professionalId) return;
    void memed.warmUp(professionalId);
  }, [memed.available, memed.warmUp, professionalId]);

  // Integração desligada ou sem chaves: nada de botão morto na interface.
  if (memed.statusLoading || !memed.available) return null;

  const blocked = disabled || !patientId;
  const tooltip = blocked
    ? disabledReason || 'Selecione o paciente para emitir a receita digital'
    : memed.environment === 'integration'
      ? 'Memed em ambiente de homologação — receitas emitidas aqui não têm validade legal'
      : 'Abre a Memed em nova aba — você continua usando agenda, inbox e prontuário aqui';

  const baseSx = isSubtle
    ? subtleSx
    : resolvedVariant === 'contained'
      ? defaultContainedSx
      : defaultOutlinedSx;

  return (
    <>
      <Tooltip title={tooltip}>
        <span style={fullWidth ? { width: '100%' } : undefined}>
          <Button
            variant={resolvedVariant}
            size={resolvedSize}
            fullWidth={fullWidth && !isSubtle}
            disabled={blocked || memed.opening}
            onClick={() =>
              void memed.openPrescription({
                patientId: patientId as string,
                professionalId,
                appointmentId,
                medicalRecordId,
              })
            }
            startIcon={
              isSubtle
                ? undefined
                : memed.opening
                  ? <CircularProgress size={16} sx={{ color: 'inherit' }} />
                  : <SignatureIcon />
            }
            sx={{ ...baseSx, ...sx }}
          >
            {memed.opening ? 'Abrindo Memed…' : resolvedLabel}
          </Button>
        </span>
      </Tooltip>

      <Snackbar
        open={Boolean(memed.error)}
        autoHideDuration={8000}
        onClose={memed.clearError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={memed.clearError} sx={{ maxWidth: 520 }}>
          {memed.error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(memed.notice)}
        autoHideDuration={5000}
        onClose={memed.clearNotice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={memed.clearNotice}>
          {memed.notice}
        </Alert>
      </Snackbar>
    </>
  );
};

/** Snackbars de erro/sucesso ao abrir a Memed fora do botão (ex.: menu do painel). */
export function MemedPrescriptionFeedback({
  error,
  notice,
  clearError,
  clearNotice,
}: {
  error: string | null;
  notice: string | null;
  clearError: () => void;
  clearNotice: () => void;
}) {
  return (
    <>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={8000}
        onClose={clearError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={clearError} sx={{ maxWidth: 520 }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={clearNotice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={clearNotice}>
          {notice}
        </Alert>
      </Snackbar>
    </>
  );
}

export default MemedPrescriptionButton;
